import { META_EVENTS, META_PIXEL_ENABLED, META_PIXEL_ID } from 'config/metaConfig';

let metaPixelInitialized = false;

type FbqFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  push?: FbqFunction;
  loaded?: boolean;
  version?: string;
};

type FbqWindow = Window & {
  fbq?: FbqFunction;
  _fbq?: FbqFunction;
};

const getFbqWindow = (): FbqWindow | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window as FbqWindow;
};

export const isMetaPixelEnabled = (): boolean => Boolean(META_PIXEL_ENABLED && META_PIXEL_ID);

export const isMetaPixelInitialized = (): boolean => metaPixelInitialized;

const loadMetaPixelScript = (): void => {
  const fbqWindow = getFbqWindow();
  if (!fbqWindow || typeof document === 'undefined' || !META_PIXEL_ID) {
    return;
  }

  const existingFbq = fbqWindow.fbq;
  if (existingFbq) {
    return;
  }

  const fbq: FbqFunction = function fbqQueue(...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
      return;
    }
    fbq.queue = fbq.queue ?? [];
    fbq.queue.push(args);
  };

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbqWindow.fbq = fbq;
  // eslint-disable-next-line no-underscore-dangle
  if (!fbqWindow._fbq) {
    // eslint-disable-next-line no-underscore-dangle
    fbqWindow._fbq = fbq;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
};

export const initMetaPixel = (): void => {
  if (
    metaPixelInitialized ||
    !isMetaPixelEnabled() ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return;
  }

  loadMetaPixelScript();

  const fbqWindow = getFbqWindow();
  if (!fbqWindow?.fbq || !META_PIXEL_ID) {
    return;
  }

  fbqWindow.fbq('set', 'autoConfig', false, META_PIXEL_ID);
  fbqWindow.fbq('consent', 'grant');
  fbqWindow.fbq('init', META_PIXEL_ID);
  fbqWindow.fbq('track', META_EVENTS.pageView);
  metaPixelInitialized = true;
};

export const teardownMetaPixel = (): void => {
  if (!metaPixelInitialized || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const fbqWindow = getFbqWindow();
  fbqWindow?.fbq?.('consent', 'revoke');

  document
    .querySelectorAll('script[src*="connect.facebook.net/en_US/fbevents.js"]')
    .forEach((script) => script.remove());

  if (fbqWindow) {
    delete fbqWindow.fbq;
    // eslint-disable-next-line no-underscore-dangle
    delete fbqWindow._fbq;
  }

  metaPixelInitialized = false;
};
